<template>
  <div class="p-4 flex flex-col gap-3">
    <div class="flex items-center justify-between gap-2">
      <div class="flex items-center gap-2 min-w-0">
        <AppIcon
          :icon="
            isFailedCompletely
              ? 'fa-regular fa-circle-exclamation'
              : isComplete
                ? 'fa-regular fa-check-circle'
                : 'fa-regular fa-upload'
          "
          :class="[
            'mt-[2px] text-base shrink-0',
            isFailedCompletely
              ? 'text-error-600'
              : isComplete
                ? 'text-success-500'
                : 'text-primary-600',
          ]"
        />
        <div class="flex flex-col min-w-0">
          <span class="text-sm font-semibold text-surface-800 truncate">
            {{ titleText }}
          </span>
        </div>
      </div>

      <div class="flex items-center gap-1 shrink-0">
        <AppButtonIcon
          v-if="canClose"
          size="sm"
          icon="fa-regular fa-xmark"
          variant="ghost"
          @click="handleClose"
        />
      </div>
    </div>

    <div
      v-if="hasCounts"
      class="flex flex-col gap-2"
    >
      <AppIndicatorProgressBar
        v-if="!isComplete"
        :progress="progressPercent"
      />
      <span class="text-2xs text-surface-600">
        {{ filesProgressText }}
      </span>
      <span
        v-if="skippedFilesText"
        class="text-2xs text-surface-500"
      >
        {{ skippedFilesText }}
      </span>
      <span
        v-if="failedFilesText"
        class="text-2xs text-error-600"
      >
        {{ failedFilesText }}
      </span>
    </div>
  </div>
</template>

<script setup lang="ts">
import { storeToRefs } from 'pinia'

const props = defineProps<{
  batchId: string
  closeCallback: () => void
}>()

const testSubmissionsStore = useTestSubmissionsStore()
const { uploadBatches } = storeToRefs(testSubmissionsStore)

const batch = computed(() => uploadBatches.value[props.batchId] ?? null)

const hasCounts = computed(
  () => batch.value !== null && batch.value.totalCount > 0,
)

const progressPercent = computed(() => {
  if (!hasCounts.value || !batch.value) return 0

  return Math.round((batch.value.uploadedCount / batch.value.totalCount) * 100)
})

const isComplete = computed(
  () =>
    (hasCounts.value &&
      batch.value !== null &&
      batch.value.uploadedCount >= batch.value.totalCount) ||
    (batch.value?.batchDone ?? false),
)

const isFailedCompletely = computed(
  () =>
    isComplete.value &&
    batch.value !== null &&
    batch.value.failedCount !== undefined &&
    batch.value.failedCount >= batch.value.totalCount,
)

const canClose = computed(
  () => isComplete.value || (batch.value?.canClose ?? false),
)

// Dismiss this toast whenever the batch entry is removed — both in the normal
// completion path (after the auto-close timer) and on early bail-out (upload
// error, missing workspace, etc.) where the store clears the batch without a
// closeCallback reference.
watch(batch, (newBatch, oldBatch) => {
  if (oldBatch && !newBatch) {
    props.closeCallback()
  }
})

watch(isComplete, (complete) => {
  if (!complete) return
  if (batch.value?.failedCount && batch.value.failedCount > 0) return

  const timer = setTimeout(() => {
    testSubmissionsStore.clearUploadProgressToast(props.batchId)
    // closeCallback is invoked by the batch watcher above once the entry is removed
  }, 8000)

  onUnmounted(() => clearTimeout(timer))
})

const titleText = computed(() => {
  if (isFailedCompletely.value) return 'Upload failed'
  if (isComplete.value) return 'Submissions uploaded'
  return 'Uploading submissions'
})

const filesProgressText = computed(() => {
  if (!hasCounts.value || !batch.value) {
    return 'Starting upload...'
  }

  const total = batch.value.totalCount
  const uploaded = batch.value.uploadedCount
  const pluralSuffix = total === 1 ? '' : 's'

  if (isComplete.value) {
    const uploadedSuffix = uploaded === 1 ? '' : 's'
    return `${uploaded} file${uploadedSuffix} uploaded`
  }

  return `Uploaded ${uploaded} of ${total} file${pluralSuffix}`
})

const skippedFilesText = computed(() => {
  const skipped = batch.value?.skippedFiles
  if (!skipped || skipped <= 0) return null
  const pluralSuffix = skipped === 1 ? '' : 's'
  return `${skipped} file${pluralSuffix} skipped`
})

const failedFilesText = computed(() => {
  const failed = batch.value?.failedCount
  if (!failed || failed <= 0) return null
  const pluralSuffix = failed === 1 ? '' : 's'
  return `${failed} file${pluralSuffix} failed to upload`
})

function handleClose() {
  if (!canClose.value) return

  testSubmissionsStore.clearUploadProgressToast(props.batchId)
  // closeCallback is invoked by the batch watcher above once the entry is removed
}
</script>
